import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Globe, Key, Users, GraduationCap, Store, Copy, Check, Info, Code, RefreshCw, Plus, Loader2 } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import Switch from 'components/ui/switch';
import { Card, CardContent } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { PageHeader } from 'components/ui/page-header';
import { DatePicker } from 'components/ui/date-picker';
import { DateTimePicker } from 'components/ui/date-time-picker';
import Tooltip from 'components/ui/tooltip';
import Modal from 'components/ui/Modal';
import { cn } from 'lib/utils';
import * as TestModule from 'modules/test';
import * as TestApi from 'modules/test/api';
import { isMarketplaceTest } from 'modules/test/marketplace-policy';
import { useMarketplacePublishing } from 'modules/test/hooks/useMarketplacePublishing';
import * as Invite from 'modules/invite';
import { FEATURES, useFeatureLimit } from 'modules/entitlements';
import { UpgradeFieldBadge } from 'components/entitlements';
import { useToast } from 'components/ui/use-toast';
import { useTranslation } from 'react-i18next';
import AccessDecisionCards, { type AccessDecisionOption } from './access/AccessDecisionCards';
import AccessKeysTable from './access/AccessKeysTable';
import type { AccessKeyRow } from './access/exportAccessKeys';
import MarketplaceListingCard, { type MarketplaceFormData } from './access/MarketplaceListingCard';

const pad = (n: number) => String(n).padStart(2, '0');
function toLocalInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocalInput = (v: string): string | null => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};
// Expiry uses a date-only picker (YYYY-MM-DD); codes expire at the end of that day.
const fromExpiryDate = (v: string): string | undefined =>
  v ? new Date(`${v}T23:59:59`).toISOString() : undefined;

export const TestAccessPage: React.FC = () => {
  const { t } = useTranslation();
  const { testId } = useParams<{ testId: string }>();
  const { toast } = useToast();
  const [accessType, setAccessType] = useState<string>('public_link');
  const [attemptsPerRespondent, setAttemptsPerRespondent] = useState(1);
  // Retakes per candidate are plan-capped (Growth+ raises the cap). null =
  // unlimited (Pro), 0 = not yet seeded onto the plan → no cap shown.
  const retakesLimit = useFeatureLimit(FEATURES.TEST_RETAKES_MAX);
  const retakesCapped = retakesLimit != null && retakesLimit > 0;
  const [showEmbed, setShowEmbed] = useState(false);
  // Public candidate link controls. The link is "on" whenever the access type
  // is public_link; the activation window + result visibility live here too.
  const [availableFrom, setAvailableFrom] = useState('');
  const [availableUntil, setAvailableUntil] = useState('');
  const [showResults, setShowResults] = useState(true);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  // Access-code (ZT-) controls: batch size + optional expiry.
  const [codeCount, setCodeCount] = useState(1);
  const [codeExpiresAt, setCodeExpiresAt] = useState('');
  const queryClient = useQueryClient();
  const { item: test, isLoading, refetch } = TestModule.Hooks.useSingle({
    id: testId || '',
    enabled: Boolean(testId),
  });
  const { canPublish: marketplaceEnabled, refetch: refreshMarketplace } = useMarketplacePublishing(test?.organizationId);

  const marketplaceDataRef = useRef<MarketplaceFormData | null>(null);
  const handleMarketplaceDataChange = useCallback((data: MarketplaceFormData) => {
    marketplaceDataRef.current = data;
  }, []);

  const ACCESS_OPTIONS: AccessDecisionOption[] = [
    {
      value: 'public_link',
      title: t('access_card_public_title'),
      description: t('access_card_public_desc'),
      icon: <Globe className="h-5 w-5" />,
    },
    {
      value: 'private_code',
      title: t('access_card_codes_title'),
      description: t('access_card_codes_desc'),
      icon: <Key className="h-5 w-5" />,
    },
    {
      value: 'team',
      title: t('access_card_group_title'),
      description: t('access_card_group_desc'),
      icon: <GraduationCap className="h-5 w-5" />,
    },
    ...(marketplaceEnabled ? [{
      value: 'marketplace',
      title: t('access_card_marketplace_title'),
      description: t('access_card_marketplace_desc'),
      icon: <Store className="h-5 w-5" />,
    }] : []),
  ];

  const { data: teamAccessRes } = useQuery({
    queryKey: ['test', testId, 'team-access'],
    enabled: Boolean(testId) && accessType === 'team',
    queryFn: async () => (await TestApi.TeamAccess(testId as string)).data,
  });
  const accessTeams = (teamAccessRes as { data?: Array<{ id: string; name: string }> })?.data ?? [];
  const { data: inviteCodes = [], isLoading: codesLoading } = useQuery({
    queryKey: ['invites', 'test', testId],
    enabled: Boolean(testId) && accessType === 'private_code',
    queryFn: async () => (await Invite.Api.GetByTest(testId as string)).data as AccessKeyRow[],
  });
  const generateMutation = useMutation({
    mutationFn: (count: number) => Invite.Api.Generate(testId as string, count, fromExpiryDate(codeExpiresAt)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invites', 'test', testId] });
      toast.success(t('codes_generated'));
    },
    onError: () => toast.error(t('error')),
  });
  const deleteCodeMutation = useMutation({
    mutationFn: (id: string) => Invite.Api.Delete(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites', 'test', testId] }),
    onError: () => toast.error(t('error')),
  });

  // Hydrate the form once, on first load. Later refetches (ensure-token,
  // Regenerate, Save) return a fresh `test` object but must NOT clobber the
  // author's in-progress, unsaved selection/window/visibility edits. The public
  // token itself is read straight from `test` in render, so it still updates.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (isLoading || !test?.id || hydratedRef.current) return;
    hydratedRef.current = true;
    const config = (test as { config?: { access?: { accessType?: string; attemptsPerRespondent?: number } } })?.config;
    const access = config?.access;
    if (isMarketplaceTest(test)) setAccessType('marketplace');
    else if (access?.accessType) setAccessType(access.accessType);
    if (access?.attemptsPerRespondent != null) setAttemptsPerRespondent(access.attemptsPerRespondent);
    const tt = test as { availableFrom?: string; availableUntil?: string; showResultsToRespondent?: boolean };
    setAvailableFrom(toLocalInput(tt?.availableFrom));
    setAvailableUntil(toLocalInput(tt?.availableUntil));
    setShowResults(tt?.showResultsToRespondent !== false);
  }, [test, isLoading]);

  const isPublicLink = accessType === 'public_link';
  const isMarketplace = accessType === 'marketplace';
  const publicToken = (test as { publicLink?: { token?: string } })?.publicLink?.token;
  const candidateLink = publicToken
    ? `${typeof window !== 'undefined' ? window.location.origin : ''}/s/${publicToken}`
    : '';
  // Public link is meant to exist the moment the page opens. New tests are
  // created with a token; for older tests without one, enable it once on load
  // (generates a token server-side) so the shareable URL appears without a
  // manual Save. Guest access still requires the test to be ACTIVE.
  const ensuredPublicRef = useRef(false);
  useEffect(() => {
    if (!testId || !test?.id) return;
    if (!isPublicLink || publicToken || isMarketplaceTest(test)) return;
    if (ensuredPublicRef.current) return;
    ensuredPublicRef.current = true;
    TestApi.UpdatePublicLink(testId, { enabled: true })
      .then(() => refetch())
      .catch(() => {
        ensuredPublicRef.current = false;
      });
  }, [testId, test, isPublicLink, publicToken, refetch]);

  const handleCopyLink = () => {
    if (candidateLink) {
      navigator.clipboard.writeText(candidateLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success(t('link_copied'), { description: t('test_link_copied_to_clipboard') });
    }
  };
  const embedCode = `<iframe src="${candidateLink}" width="100%" height="800" style="border:0" allowfullscreen></iframe>`;
  const handleCopyEmbed = () => {
    navigator.clipboard.writeText(embedCode);
    toast.success(t('code_copied'));
  };
  const handleRegenerate = async () => {
    if (!testId) return;
    setRegenerating(true);
    try {
      await TestApi.RegeneratePublicLink(testId);
      await refetch();
      toast.success(t('link_copied'), { description: t('test_link_copied_to_clipboard') });
    } catch {
      toast.error(t('error'));
    } finally {
      setRegenerating(false);
    }
  };
  // A window that closes before it opens can never let a candidate in.
  const windowInvalid =
    isPublicLink && Boolean(availableFrom) &&
    Boolean(availableUntil) &&
    new Date(availableUntil).getTime() <= new Date(availableFrom).getTime();

  // Save the mode before its settings so requests cannot overwrite each other.
  const handleSave = async () => {
    if (!testId || windowInvalid || (isMarketplace && !marketplaceEnabled)) return;
    setSaving(true);
    try {
      await TestApi.UpdateConfigAccess(testId, {
        accessType,
        ...(!isMarketplace ? { attemptsPerRespondent } : {}),
      });
      if (isPublicLink) {
        await TestApi.UpdatePublicLink(testId, {
          enabled: true,
          availableFrom: fromLocalInput(availableFrom),
          availableUntil: fromLocalInput(availableUntil),
          showResultsToRespondent: showResults,
        });
      }

      if (isMarketplace && marketplaceDataRef.current) {
        const m = marketplaceDataRef.current;
        await TestApi.Update({
          id: testId,
          category: m.category,
          subjectKey: m.subjectKey,
          variantNumber: m.variantNumber,
          isLeadVariant: m.isLeadVariant,
          difficultyLevel: m.difficultyLevel,
          description: m.description.trim(),
          tags: m.tags,
          accessType: m.accessType,
          priceSom: m.accessType === 'PAID' ? m.priceSom : 0,
        });
      }

      queryClient.invalidateQueries({ queryKey: ['public-test-categories'] });
      await refetch();
      toast.success(t('saved'));
    } catch (err: any) {
      if (err?.response?.data?.code === 'MARKETPLACE_NOT_ENABLED') void refreshMarketplace();
      toast.error(t('error'), {
        description: err?.response?.data?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading || !test) {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-muted border-t-foreground" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <PageHeader
        title={t('access')}
        description={t('access_page_subtitle')}
        actions={
          <Button onClick={handleSave} disabled={saving || windowInvalid || (isMarketplace && !marketplaceEnabled)}>
            {saving && (
              <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            )}
            {t('save')}
          </Button>
        }
      />

      <AccessDecisionCards
        legend={t('access_who_can_take')}
        options={ACCESS_OPTIONS}
        value={accessType}
        onChange={setAccessType}
      />

      {!isMarketplace && <Card data-tour="test-access-share-box" id="test-access-share-box">
        <CardContent className="space-y-6 p-6">
          {isPublicLink && (
            <div className="space-y-5">
              {candidateLink ? (
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Input
                      value={candidateLink}
                      readOnly
                      onClick={(e) => (e.target as HTMLInputElement).select()}
                      className="h-9 font-mono text-xs text-muted-foreground select-all bg-muted/20 sm:text-sm"
                      aria-label={t('public_candidate_link')}
                    />
                    <Tooltip content={copied ? t('copied') : t('copy')}>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className={cn(
                          'h-9 w-9 shrink-0 transition-colors',
                          copied && 'border-emerald-500/50 text-emerald-600 dark:text-emerald-400 bg-emerald-50/50 dark:bg-emerald-950/20'
                        )}
                        onClick={handleCopyLink}
                        aria-label={t('copy')}
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </Tooltip>
                    <Tooltip content={t('regenerate_link')}>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="h-9 w-9 shrink-0 text-muted-foreground hover:text-foreground"
                        onClick={handleRegenerate}
                        disabled={regenerating}
                        aria-label={t('regenerate_link')}
                      >
                        <RefreshCw className={cn('h-4 w-4', regenerating && 'animate-spin')} />
                      </Button>
                    </Tooltip>
                  </div>
                  <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Info className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    <span>{t('no_login_required_share_link')}</span>
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('save_to_generate_public_link')}</p>
              )}

              <div className="space-y-2 pt-1 border-t border-border/50">
                <div className="flex flex-wrap items-end gap-3 sm:gap-4">
                  <div className="w-full sm:w-[250px]">
                    <div className="mb-1 flex items-center justify-between">
                      <label className="block text-xs font-medium text-muted-foreground">
                        {t('available_from')}
                      </label>
                      {availableFrom && (
                        <button
                          type="button"
                          onClick={() => setAvailableFrom('')}
                          className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                          title={t('clear')}
                        >
                          {t('clear')}
                        </button>
                      )}
                    </div>
                    <DateTimePicker
                      value={availableFrom}
                      onChange={setAvailableFrom}
                      defaultTime="00:00"
                      ariaLabel={t('available_from')}
                    />
                  </div>

                  <span className="hidden sm:inline-block pb-2 text-xs text-muted-foreground font-medium">—</span>

                  <div className="w-full sm:w-[250px]">
                    <div className="mb-1 flex items-center justify-between">
                      <label className="block text-xs font-medium text-muted-foreground">
                        {t('available_until')}
                      </label>
                      {availableUntil && (
                        <button
                          type="button"
                          onClick={() => setAvailableUntil('')}
                          className="text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                          title={t('clear')}
                        >
                          {t('clear')}
                        </button>
                      )}
                    </div>
                    <DateTimePicker
                      value={availableUntil}
                      onChange={setAvailableUntil}
                      min={availableFrom || undefined}
                      defaultTime="23:59"
                      ariaLabel={t('available_until')}
                    />
                  </div>
                </div>
                {windowInvalid && (
                  <p className="text-xs text-destructive">{t('window_order_invalid')}</p>
                )}
              </div>

              <div className="flex items-center justify-between pt-1 border-t border-border/50">
                <p className="text-sm font-medium text-foreground">{t('show_results_to_respondents')}</p>
                <Switch checked={showResults} onChange={setShowResults} />
              </div>
            </div>
          )}

          {accessType === 'private_code' && (
            <div className="space-y-5">
              {/* Generation wizard — quantity, usage, expiry, then generate. */}
              <div className="rounded-lg border border-border bg-muted/20 p-4">
                {/* Fields stack full-width on phones, pair up on tablets, and sit
                    on one line with the button from lg up. Fixed field widths
                    left ragged half-empty rows on narrow screens. */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:items-end lg:grid-cols-[6rem_minmax(0,1fr)_minmax(0,1fr)_auto] lg:gap-4">
                  <div className="min-w-0">
                    <label className="mb-1 block text-[13px] font-medium text-muted-foreground">
                      {t('access_quantity')}
                    </label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={codeCount}
                      onChange={(e) => setCodeCount(Math.min(100, Math.max(1, Number(e.target.value) || 1)))}
                      aria-label={t('access_quantity')}
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="mb-1 block text-[13px] font-medium text-muted-foreground">
                      {t('access_usage')}
                    </label>
                    {/* Every access code is single-use — usage is fixed, shown for context. */}
                    <div className="flex h-9 items-center truncate rounded-md border border-border bg-background px-3 text-sm font-medium text-foreground">
                      {t('access_usage_onetime')}
                    </div>
                  </div>
                  <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                    <label className="mb-1 block text-[13px] font-medium text-muted-foreground">
                      {t('access_expires')}
                    </label>
                    <DatePicker
                      value={codeExpiresAt}
                      onChange={setCodeExpiresAt}
                      ariaLabel={t('access_expires')}
                    />
                  </div>
                  <Button
                    className="w-full sm:col-span-2 sm:w-auto sm:justify-self-end lg:col-span-1"
                    onClick={() => generateMutation.mutate(codeCount)}
                    disabled={generateMutation.isPending}
                  >
                    {generateMutation.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-4 w-4" />
                    )}
                    {t('generate_codes')}
                  </Button>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">{t('access_codes_single_use_note')}</p>
              </div>

              <AccessKeysTable
                rows={inviteCodes}
                isLoading={codesLoading}
                onDelete={(id) => deleteCodeMutation.mutate(id)}
                deletingId={deleteCodeMutation.isPending ? deleteCodeMutation.variables : null}
              />
            </div>
          )}

          {accessType === 'team' && (
            <div className="space-y-3">
              <div className="flex items-start gap-2 rounded-lg border border-border bg-background p-3">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <p className="text-sm text-foreground/80">{t('access_team_help')}</p>
              </div>
              {accessTeams.length > 0 ? (
                <div>
                  <p className="mb-2 text-sm font-medium text-foreground">{t('access_team_with_access')}</p>
                  <div className="flex flex-wrap gap-2">
                    {accessTeams.map((tm) => (
                      <Badge key={tm.id} variant="secondary" className="gap-1">
                        <Users className="h-3 w-3" />
                        {tm.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t('access_team_none')}</p>
              )}
              <Button variant="outline" size="sm" asChild>
                <Link to="/app/teams">
                  <Users className="mr-2 h-4 w-4" />
                  {t('nav.teams')}
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>}

      {isMarketplace && !marketplaceEnabled && (
        <p role="status" className="rounded-lg border border-border bg-muted/20 p-4 text-sm text-muted-foreground">
          {t('marketplace_org_disabled')}
        </p>
      )}

      {isMarketplace && marketplaceEnabled && testId && test && (
        <MarketplaceListingCard
          testId={testId}
          test={test}
          refetch={refetch}
          onMarketplaceDataChange={handleMarketplaceDataChange}
          onPublishingDenied={() => { void refreshMarketplace(); }}
        />
      )}

      <Card>
        <CardContent className="space-y-4 p-6">
          <h3 className="text-[13px] font-semibold text-foreground">{t('respondents')}</h3>
          {isMarketplace ? (
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">{t('marketplace_attempts_unlimited')}</p>
              <p className="text-xs text-muted-foreground">{t('marketplace_result_policy')}</p>
            </div>
          ) : <div>
            <div className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
              <label className="block text-sm font-medium text-foreground">{t('attempts_per_respondent')}</label>
              {retakesCapped && (
                <UpgradeFieldBadge
                  featureKey={FEATURES.TEST_RETAKES_MAX}
                  limit={retakesLimit as number}
                  requested={attemptsPerRespondent}
                  label={t('growth', 'Growth')}
                  tooltip={t('retakes_plan_hint', {
                    count: retakesLimit as number,
                    defaultValue: `Your plan allows up to {{count}} attempt(s) per candidate. Upgrade for more.`,
                  })}
                />
              )}
            </div>
            <Input
              type="number"
              min={1}
              max={retakesCapped ? (retakesLimit as number) : undefined}
              value={attemptsPerRespondent}
              onChange={(e) => setAttemptsPerRespondent(Number(e.target.value) || 1)}
              className="w-24"
            />
            <p className="mt-1 text-xs text-muted-foreground">{t('number_of_times_a_respondent_can_take_test')}</p>
          </div>}
          {/* Button is whitespace-nowrap by default, so this label (uz: "Testni
              veb-saytga qanday joylashtirish mumkin?") ran straight off the
              card and out of the viewport. Let it wrap and grow instead. */}
          {isPublicLink && <Button
            variant="ghost"
            size="sm"
            className="h-auto min-h-9 w-full justify-start whitespace-normal py-1.5 text-left text-muted-foreground sm:w-auto"
            onClick={() => setShowEmbed(true)}
          >
            <Code className="mr-2 h-4 w-4 shrink-0" />
            {t('how_to_embed_a_test_on_a_website')}
          </Button>}
        </CardContent>
      </Card>

      <Modal
        isOpen={showEmbed}
        onClose={() => setShowEmbed(false)}
        title={t('embed_code')}
        description={t('embed_code_description')}
        size="lg"
      >
        <div className="space-y-4">
          {candidateLink ? (
            <>
              <pre className="whitespace-pre-wrap break-all rounded-lg bg-muted p-4 font-mono text-sm text-foreground">
                {embedCode}
              </pre>
              <Button onClick={handleCopyEmbed}>
                <Copy className="mr-2 h-4 w-4" />
                {t('copy_code')}
              </Button>
            </>
          ) : (
            <div className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 dark:bg-amber-950/30">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-sm text-amber-800 dark:text-amber-200">{t('enable_the_public_link_first')}</p>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default TestAccessPage;
